import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Button, Form, InputGroup, ListGroup, Alert, Spinner, Badge } from 'react-bootstrap';
import { Eye, EyeSlash, Clipboard } from 'react-bootstrap-icons';
import { checkConnectionStatus, connectUpstox, disconnectUpstox } from '../slices/upstoxConnection';
import { login, logout, requestToken, checkTokenRequestStatus } from '../slices/upstoxAuthSlice';

export default function UpstoxLogin() {
  const dispatch = useDispatch();
  const { isConnected, error, loading, connectionName: savedConnectionName } = useSelector(state => state.upstoxConnection) || {};
  const { tokenExpiry, isAuthenticated } = useSelector(state => state.upstoxAuthSlice) || {};
  
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [showBearerToken, setShowBearerToken] = useState(false);
  const [connectionName, setConnectionName] = useState('App');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [bearerToken, setBearerToken] = useState('');
  const [tokenExpiryDate, setTokenExpiryDate] = useState(null); 
  const [isLoading, setIsLoading] = useState(true);
  const [requestId, setRequestId] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);

  const handleReauthorize = useCallback(() => dispatch(login()), [dispatch]);

  const handleBearerTokenSubmit = (e) => {
    e.preventDefault();
    if (bearerToken) {
      sessionStorage.setItem('upstoxToken', bearerToken);
      window.location.reload(); // Reload to update the auth state
    }
  };

  useEffect(() => {
    const checkConnection = async () => {
      await dispatch(checkConnectionStatus());
      setIsLoading(false);
    };

    checkConnection();
  }, [dispatch]);

  useEffect(() => {
    dispatch(checkConnectionStatus());
    if (tokenExpiry) {
      const expiryDate = new Date(tokenExpiry);
      setTokenExpiryDate(expiryDate.toLocaleString());
      if (expiryDate < new Date()) handleReauthorize();
    }
  }, [dispatch, tokenExpiry, handleReauthorize]);

  // Start polling for token status
  useEffect(() => {
    if (requestId) {
      const interval = setInterval(() => {
        dispatch(checkTokenRequestStatus(requestId));
      }, 5000); // Poll every 5 seconds
      setPollingInterval(interval);
    }
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [requestId, dispatch]);

  const handleTokenRequest = async (e) => {
    e.preventDefault();
    try {
      const result = await dispatch(requestToken({
        client_id: apiKey,
        client_secret: apiSecret
      })).unwrap();
      
      if (result.status === 'success') {
        setRequestId(result.data.request_id);
        // Show notification to user
        alert('Please check your Upstox app or WhatsApp for the authorization request');
      }
    } catch (error) {
      console.error('Token request failed:', error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(connectUpstox({ connectionName, apiKey, apiSecret }));
  };

  const handleDisconnect = () => {
    dispatch(disconnectUpstox());
    dispatch(logout());
  };

  const toggleVisibility = (setter) => () => setter(prev => !prev);
  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  if (isLoading) {
    return (
      <Card className="w-100 mx-auto" style={{ maxWidth: '48rem' }}>
        <Card.Body className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="w-100 mx-auto" style={{ maxWidth: '48rem' }}>
      <Card.Header>
        <Card.Title as="h2">{isConnected ? (isAuthenticated ? 'Connected' : 'Not Authorized') : 'Setup Upstox'}</Card.Title>
      </Card.Header>
      <Card.Body>
        {isConnected ? (
          isAuthenticated ? (
            <>
              <Alert variant="success">
                Your Upstox account is connected and authorized with the connection name: {savedConnectionName}.
              </Alert>
              {tokenExpiryDate && <Badge bg="info">Token Expiry Date: {tokenExpiryDate}</Badge>}
              <Button variant="danger" onClick={handleReauthorize} className="w-100 mt-3">Reauthorize Upstox</Button>
              <Button variant="danger" onClick={handleDisconnect} className="w-100 mt-3">Disconnect Upstox</Button>
            </>
          ) : (
            <>
              <Alert variant="warning">
                Your Upstox account is connected but not authorized. Please reauthorize to generate a new token.
              </Alert>
              <Button variant="primary" onClick={handleReauthorize} className="w-100 mt-3">Reauthorize Upstox</Button>
              <Button variant="danger" onClick={handleDisconnect} className="w-100 mt-3">Disconnect Upstox</Button>
            </>
          )
        ) : (
          <>
            <Form onSubmit={handleTokenRequest} className="mb-4">
              <Form.Group className="mb-3">
                <Form.Label><strong>Request Token Authorization</strong></Form.Label>
                <InputGroup className="mb-3">
                  <Form.Control 
                    type={showApiKey ? "text" : "password"} 
                    value={apiKey} 
                    onChange={(e) => setApiKey(e.target.value)} 
                    placeholder="Enter your API Key"
                    required 
                  />
                  <Button variant="outline-secondary" onClick={toggleVisibility(setShowApiKey)}>
                    {showApiKey ? <EyeSlash /> : <Eye />}
                  </Button>
                </InputGroup>
                <InputGroup>
                  <Form.Control 
                    type={showApiSecret ? "text" : "password"} 
                    value={apiSecret} 
                    onChange={(e) => setApiSecret(e.target.value)} 
                    placeholder="Enter your API Secret"
                    required 
                  />
                  <Button variant="outline-secondary" onClick={toggleVisibility(setShowApiSecret)}>
                    {showApiSecret ? <EyeSlash /> : <Eye />}
                  </Button>
                </InputGroup>
                <Form.Text className="text-muted">
                  Enter your API Key and Secret. You will receive a notification in your Upstox app or WhatsApp to approve the request.
                </Form.Text>
              </Form.Group>
              <Button variant="primary" type="submit" className="w-100">Request Authorization</Button>
            </Form>

            {requestId && (
              <Alert variant="info" className="mt-3">
                Waiting for your approval. Please check your Upstox app or WhatsApp for the authorization request.
                <br />
                Request ID: {requestId}
              </Alert>
            )}

            <hr className="my-4" />
            <h4 className="mb-3">Or Enter Bearer Token Directly:</h4>
            
            <Form onSubmit={handleBearerTokenSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Bearer Token</Form.Label>
                <InputGroup>
                  <Form.Control 
                    type={showBearerToken ? "text" : "password"} 
                    value={bearerToken} 
                    onChange={(e) => setBearerToken(e.target.value)} 
                    placeholder="Enter your Bearer token"
                    required 
                  />
                  <Button variant="outline-secondary" onClick={toggleVisibility(setShowBearerToken)}>
                    {showBearerToken ? <EyeSlash /> : <Eye />}
                  </Button>
                </InputGroup>
              </Form.Group>
              <Button variant="success" type="submit" className="w-100">Use Bearer Token</Button>
            </Form>
          </>
        )}
      </Card.Body>
    </Card>
  );
}